import { Magnitudes } from "@/component-library/constants/Magnitudes";
import Color from "color";

// Core Brand Colors
export const primaryBlueColor = Color("#004CA3"); // Royal Blue
export const secondaryDepthColor = Color("#0D447B"); // Modern Navy
export const amberColor = Color("#FFB347"); // Amber (Primary CTA)
export const tealColor = Color("#00BFA6"); // Teal

// Neutral Colors - Backgrounds & Surfaces
export const whiteColor = Color("#FFFFFF"); // White
export const softWhiteColor = Color("#f0f0f0"); // Soft White
export const lightGreyColor = Color("#CAD2DC"); // Light Grey
export const lightBlueGreyColor = Color("#E6F2F7"); // Light Blue-Grey

// Typography & Contrast
export const darkGreyColor = Color("#2E2E2E"); // Main body text
export const blackColor = Color("#2E2E2E"); // Alias for darkGreyColor
export const pitchBlackColor = Color("#000000"); // Pure black (minimal use)

// Status & Functional Colors
export const successColor = Color("#27AE60");
export const warningColor = Color("#F4A259");
export const errorColor = Color("#E74C3C");
export const infoHighlightBlueColor = Color("#87C3F3");
export const softCoralColor = Color("#F9D5C2");

// Legacy colors (for backward compatibility)
export const greenColor = Color("#27AE60"); // Updated to success color
export const redColor = Color("#E74C3C"); // Updated to error color
export const orangeColor = Color("#FFB347"); // Alias for amberColor

// Derived colors
export const primaryBlueFadedColor = Color("#374f77");
export const primaryBlueDarkColor = secondaryDepthColor; // Use Modern Navy
export const lightSurfaceColor = softWhiteColor;
export const mediumSurfaceColor = lightGreyColor;
export const darkSurfaceColor = lightBlueGreyColor;
export const disabledSurfaceColor = Color("#6b6d7a");
export const loadingSurfaceColor = lightBlueGreyColor;
export const navigationBlueColor = secondaryDepthColor; // Use Modern Navy

export const outlineColor = Color("#CED9E7");
export const lightOutlineColor = Color("#eceef1");
export const modernOutlineColor = Color("#cad2dcb3");

export const textColor = darkGreyColor; // #2E2E2E - Main body text
export const secondaryTextColor = Color("#2E2E2E");
export const tertiaryTextColor = Color("#7c7c8c");
export const lightDisabledTextColor = Color("#e0e1e3");
export const darkDisabledTextColor = Color("#868c94");
export const extraDarkDisabledTextColor = Color("#6a6a6a");

export const histogramBarColor = Color("#b6c0d2");
export const sliderColor = histogramBarColor.darken(0.1);
export const visualisationConnectionColor = Color("#D1DAE7");

/**
 * Theme for the app. Any other theme should use this one as the type, i.e.
 * [typeof lightTheme] so that all the properties are the same between themes.
 */
export const lightTheme = {
    colors: {
        // Core Brand Colors
        brand: {
            primary: primaryBlueColor.toString(), // Royal Blue #004CA3
            secondaryDepth: secondaryDepthColor.toString(), // Modern Navy #0D447B
            amber: amberColor.toString(), // Amber #FFB347
            teal: tealColor.toString(), // Teal #00BFA6
        },
        basic: {
            primary: primaryBlueColor.toString(),
            secondary: errorColor.toString(),
            white: whiteColor.toString(),
            black: darkGreyColor.toString(), // Use dark grey instead of pure black
            orange: amberColor.toString(), // Use amber instead
            red: errorColor.toString(),
            lightGrey: lightGreyColor.toString(),
        },
        // Neutral Colors
        backgrounds: {
            white: whiteColor.toString(), // #FFFFFF
            softWhite: softWhiteColor.toString(), // #FAFAFA
            lightGrey: lightGreyColor.toString(), // #F4F4F4
            lightBlueGrey: lightBlueGreyColor.toString(), // #E6F2F7
        },
        typography: {
            darkGrey: darkGreyColor.toString(), // #2E2E2E
            black: blackColor.toString(),
        },
        // Status Colors
        status: {
            success: successColor.toString(), // #27AE60
            warning: warningColor.toString(), // #F4A259
            error: errorColor.toString(), // #E74C3C
            info: infoHighlightBlueColor.toString(), // #87C3F3
            softCoral: softCoralColor.toString(), // #F9D5C2
        },
        semantic: {
            error: {
                ultraLight: errorColor.lighten(1.226).toString(),
                veryLight: errorColor.lighten(0.68).toString(),
                lighter: errorColor.lighten(0.2).toString(),
                default: errorColor.toString(), // #E74C3C
                darker: errorColor.darken(0.2).toString(),
            },
            warning: {
                lighter: warningColor.lighten(0.2).toString(),
                default: warningColor.toString(), // #F4A259
                darker: warningColor.darken(0.2).toString(),
            },
            attention: {
                lighter: amberColor.lighten(0.2).toString(),
                default: amberColor.toString(), // #FFB347
                darker: amberColor.darken(0.2).toString(),
            },
            success: {
                lighter: successColor.lighten(0.2).toString(),
                default: successColor.toString(), // #27AE60
                darker: successColor.darken(0.2).toString(),
            },
            facility: {
                lighter: redColor.lighten(0.4).toString(),
                default: redColor.lighten(0.3).toString(),
                darker: redColor.lighten(0.2).toString(),
            },
            capacity: {
                lighter: greenColor.lighten(0.2).toString(),
                default: greenColor.toString(),
                darker: greenColor.darken(0.2).toString(),
            },
            other: {
                lighter: blackColor.lighten(2.5).toString(),
                default: blackColor.lighten(0.3).toString(),
                darker: blackColor.lighten(0.2).toString(),
            },
            interactive: {
                lightDesaturated: primaryBlueColor
                    .lighten(0.3)
                    .desaturate(0.7)
                    .toString(),
                veryLightDesaturated: primaryBlueColor
                    .lighten(0.65)
                    .desaturate(0.7)
                    .toString(),
                lighter: primaryBlueColor
                    .lighten(0.4)
                    .desaturate(0.3)
                    .toString(),
                default: primaryBlueColor.toString(),
                darker: primaryBlueColor.darken(0.2).toString(),
                transparent: primaryBlueColor.alpha(0).toString(),
                disabledInteractive: primaryBlueColor
                    .lighten(0.95)
                    .desaturate(0.8)
                    .toString(),
            },
            secondaryInteractive: {
                veryLight: primaryBlueFadedColor
                    .lighten(0.5)
                    .desaturate(0.2)
                    .toString(),
                lighter: primaryBlueFadedColor.lighten(0.08).toString(),
                default: primaryBlueFadedColor.toString(),
                darker: primaryBlueFadedColor.darken(0.2).toString(),
            },
        },
        text: {
            primary: darkGreyColor.toString(), // #2E2E2E - Main body text per brand guidelines
            secondary: secondaryTextColor.toString(),
            tertiary: tertiaryTextColor.toString(),
            darkDisabled: darkDisabledTextColor.toString(),
            extraDarkDisabledTextColor: extraDarkDisabledTextColor.toString(),
            lightDisabled: lightDisabledTextColor.toString(),
            // Brand-specific text colors for proper contrast
            onAmber: darkGreyColor.toString(), // Dark grey text on amber background (#2E2E2E on #FFB347)
            onWhite: darkGreyColor.toString(), // Dark grey text on white background (#2E2E2E on #FFFFFF)
            onSoftWhite: darkGreyColor.toString(), // Dark grey text on soft white (#2E2E2E on #FAFAFA)
            onLightGrey: darkGreyColor.toString(), // Dark grey text on light grey (#2E2E2E on #F4F4F4)
            onBlue: whiteColor.toString(), // White text on blue background (#FFFFFF on #004CA3)
            onNavy: whiteColor.toString(), // White text on navy background (#FFFFFF on #0D447B)
            onTeal: whiteColor.toString(), // White text on teal background (#FFFFFF on #00BFA6)
            onSuccess: whiteColor.toString(), // White text on success background (#FFFFFF on #27AE60)
            onError: whiteColor.toString(), // White text on error background (#FFFFFF on #E74C3C)
            onWarning: darkGreyColor.toString(), // Dark grey text on warning background (#2E2E2E on #F4A259)
        },
        surface: {
            // White backgrounds
            white: {
                o30: whiteColor.alpha(0.3).toString(),
                o50: whiteColor.alpha(0.5).toString(),
                o80: whiteColor.alpha(0.8).toString(),
                o90: whiteColor.alpha(0.9).toString(),
                o100: whiteColor.toString(), // #FFFFFF
            },
            // Soft White backgrounds
            softWhite: {
                o50: softWhiteColor.alpha(0.5).toString(),
                o80: softWhiteColor.alpha(0.8).toString(),
                o90: softWhiteColor.alpha(0.9).toString(),
                o100: softWhiteColor.toString(), // #FAFAFA
            },
            // Light Grey backgrounds
            lightGrey: {
                o50: lightGreyColor.alpha(0.5).toString(),
                o80: lightGreyColor.alpha(0.8).toString(),
                o90: lightGreyColor.alpha(0.9).toString(),
                o100: lightGreyColor.toString(), // #F4F4F4
            },
            // Light Blue-Grey backgrounds
            lightBlueGrey: {
                o20: lightBlueGreyColor.alpha(0.2).toString(),
                o50: lightBlueGreyColor.alpha(0.5).toString(),
                o80: lightBlueGreyColor.alpha(0.8).toString(),
                o90: lightBlueGreyColor.alpha(0.9).toString(),
                o100: lightBlueGreyColor.toString(), // #E6F2F7
            },
            // Legacy surface names for backward compatibility
            ultraLightSurface: {
                o30: whiteColor.alpha(0.3).toString(),
                o50: whiteColor.alpha(0.5).toString(),
                o80: whiteColor.alpha(0.8).toString(),
                o90: whiteColor.alpha(0.9).toString(),
                o100: whiteColor.toString(),
            },
            lightSurface: {
                o50: softWhiteColor.alpha(0.5).toString(),
                o80: softWhiteColor.alpha(0.8).toString(),
                o90: softWhiteColor.alpha(0.9).toString(),
                o100: softWhiteColor.toString(),
            },
            mediumSurface: {
                o10: lightGreyColor.alpha(0.1).toString(),
                o30: lightGreyColor.alpha(0.3).toString(),
                o50: lightGreyColor.alpha(0.5).toString(),
                o80: lightGreyColor.alpha(0.8).toString(),
                o90: lightGreyColor.alpha(0.9).toString(),
                o100: lightGreyColor.toString(),
            },
            darkSurface: {
                o20: lightBlueGreyColor.alpha(0.2).toString(),
                o50: lightBlueGreyColor.alpha(0.7).toString(),
                o80: lightBlueGreyColor.alpha(0.8).toString(),
                o90: lightBlueGreyColor.alpha(0.9).toString(),
                o100: lightBlueGreyColor.toString(),
            },
            disabledSurface: {
                o30: disabledSurfaceColor.alpha(0.3).toString(),
                o50: disabledSurfaceColor.alpha(0.5).toString(),
                o80: disabledSurfaceColor.alpha(0.8).toString(),
                o90: disabledSurfaceColor.alpha(0.9).toString(),
                o100: disabledSurfaceColor.toString(),
            },
            loadingSurface: {
                startAndEndAnimationAtThisColor: loadingSurfaceColor.toString(),
                animateToThisColor: loadingSurfaceColor
                    .lighten(0.14)
                    .toString(),
            },
            outline: outlineColor.toString(),
            lightOutline: lightOutlineColor.toString(),
        },
        layoutSurface: {
            invertedSurface: {
                o20: mediumSurfaceColor.darken(0.8).alpha(0.2).toString(),
                o50: mediumSurfaceColor.darken(0.8).alpha(0.7).toString(),
                o80: mediumSurfaceColor.darken(0.8).alpha(0.8).toString(),
                o90: mediumSurfaceColor.darken(0.8).alpha(0.9).toString(),
                o100: mediumSurfaceColor
                    .darken(0.5)
                    .desaturate(0.75)
                    .toString(),
            },
            navigationSurface: {
                o90: secondaryDepthColor.alpha(0.9).toString(),
                o100: secondaryDepthColor.toString(), // Modern Navy #0D447B
                lighter: secondaryDepthColor.lighten(0.1).toString(),
                darker: secondaryDepthColor.darken(0.1).toString(),
            },
        },
        charts: {
            histogramBarColor: histogramBarColor.toString(),
            sliderColor: histogramBarColor.toString(),
            sliderDisabledColor: sliderColor.toString(),
            rangeColor: primaryBlueColor.toString(),
            sliderTickColor: sliderColor.lighten(0.3).toString(),
            connectionColor: visualisationConnectionColor.toString(),
        },
    },
    shadows: {
        subtle: `0px 1.5px 5px 0px ${blackColor.alpha(0.04).toString()}`,
        small: `0px 2.5px 10px 0px ${blackColor.alpha(0.05).toString()}`,
        diffuse: `0px 10px 40px 0px rgba(27, 47, 69, 0.05)`,
        modern: `0px 4px 4px 0px rgba(17, 12, 46, 0.08)`,
        interactive: `0px 2.5px 10px 0px ${primaryBlueColor.alpha(0.08).toString()}, 0px 1px 3px 0px ${primaryBlueColor
            .alpha(0.03)
            .toString()}`,
    },
    styles: {
        surfaceOutline: {
            light: `${Magnitudes.borderWidthInPx.s}px solid ${lightOutlineColor.toString()}`,
            medium: `${Magnitudes.borderWidthInPx.s}px solid ${outlineColor.toString()}`,
            dark: `${Magnitudes.borderWidthInPx.s}px solid ${outlineColor.darken(0.1).toString()}`,
            interactive: `${Magnitudes.borderWidthInPx.m}px solid ${primaryBlueColor.toString()}`,
        },
        surfaceOutlineModern: {
            light: `${Magnitudes.borderWidthInPx.s}px solid ${modernOutlineColor.lighten(0.1).toString()}`,
            medium: `${Magnitudes.borderWidthInPx.s}px solid ${modernOutlineColor.toString()}`,
            dark: `${Magnitudes.borderWidthInPx.s}px solid ${modernOutlineColor.darken(0.1).toString()}`,
            interactive: `${Magnitudes.borderWidthInPx.m}px solid ${primaryBlueColor.toString()}`,
        },
        errorOutline: {
            light: `1.5px solid ${redColor.lighten(0.9).toString()}`,
            medium: `1.5px solid ${redColor.lighten(0.25).toString()}`,
            dark: `1.5px solid ${redColor.toString()}`,
        },
    },
};
